import React, { useState, useEffect, useCallback } from "react";
import { Award, Users, BookOpenCheck, Globe2, BadgeCheck, Building2, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { WHY_CHOOSE, TESTIMONIALS, PARTNERS, CLIENTS, COLLAB_IMG } from "../mock";
import { Reveal } from "./Reveal";

const ICONS = { Award, Users, BookOpenCheck, Globe2, BadgeCheck, Building2 };

export const WhyChoose = () => (
  <section className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal variant="left">
        <div className="relative">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img src={COLLAB_IMG} alt="Team" className="w-full h-[480px] object-cover"/>
          </div>
          <div className="absolute -bottom-6 -right-6 bg-[#15348C] text-white rounded-2xl p-6 shadow-2xl hidden md:block">
            <div className="text-4xl font-bold" style={{fontFamily:'Times New Roman, serif'}}>500+</div>
            <div className="text-sm text-gray-200 mt-1">Along with AI Courses</div>
          </div>
        </div>
      </Reveal>

      <Reveal variant="right" delay={80}>
        <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHY CHOOSE SKILLSGAIN</div>
        <h2 className="text-[#15348C] leading-tight">Trusted by enterprises, loved by learners.</h2>
        <p className="mt-4 text-gray-600">From individual upskilling to enterprise-wide transformation — SkillsGain delivers measurable outcomes across every learning engagement.</p>

        <div className="mt-8 grid sm:grid-cols-2 gap-5">
          {WHY_CHOOSE.map((w, i) => {
            const Icon = ICONS[w.icon];
            return (
              <Reveal key={w.title} variant="up" delay={i * 80}>
                <div className="flex gap-3 group">
                  <div className="w-11 h-11 shrink-0 rounded-lg bg-[#6BBE32]/15 flex items-center justify-center group-hover:bg-[#6BBE32] transition-colors">
                    <Icon size={20} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <div>
                    <h4 className="font-bold text-[#15348C] text-sm">{w.title}</h4>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Reveal>
    </div>
  </section>
);

// Auto-sliding logo marquee (used for Partners AND Clients)
const LogoMarquee = ({ logos, speed = "slow" }) => {
  const items = [...logos, ...logos]; // duplicate for seamless loop
  return (
    <div className="sg-marquee-mask">
      <div className={`sg-marquee-track ${speed === "fast" ? "fast" : ""}`}>
        {items.map((p, i) => (
          <div key={p.name + i} className="shrink-0 w-44 h-24 bg-white border border-gray-100 rounded-xl flex items-center justify-center px-5 shadow-sm hover:shadow-md hover:border-[#6BBE32] transition-all">
            {p.logo ? (
              <img src={p.logo} alt={p.name} className="max-h-12 max-w-[140px] object-contain"
                onError={(e) => { e.target.style.display="none"; e.target.parentElement.innerHTML = `<span class='text-[#15348C] font-bold text-sm'>${p.name}</span>`; }}
              />
            ) : (
              <span className="text-[#15348C] font-bold text-sm">{p.name}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const Partners = () => (
  <section className="py-14 bg-[#F8F9FA]">
    <div className="max-w-7xl mx-auto px-4">
      <Reveal>
        <div className="text-center mb-8">
          <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">OUR TRAINING PARTNERS</div>
          <h2 className="text-[#15348C]">Authorized by the world’s leading vendors</h2>
          <p className="mt-3 text-gray-600 max-w-2xl mx-auto">Officially certified to deliver vendor-approved training and certifications from 40+ global technology leaders.</p>
        </div>
      </Reveal>
      <LogoMarquee logos={PARTNERS} speed="slow" />
    </div>
  </section>
);

export const Clients = () => (
  <section className="py-14 bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <Reveal>
        <div className="text-center mb-8">
          <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">TRUSTED BY ENTERPRISE CLIENTS</div>
          <h2 className="text-[#15348C]">India’s top enterprises learn with SkillsGain</h2>
        </div>
      </Reveal>
      <LogoMarquee logos={CLIENTS} speed="fast" />
    </div>
  </section>
);

// Responsive Testimonials carousel
export const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w < 640 ? 1 : w < 1024 ? 2 : 3);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  const total = TESTIMONIALS.length;
  const maxIdx = Math.max(0, total - perView);

  const next = useCallback(() => setIdx(i => (i >= maxIdx ? 0 : i + 1)), [maxIdx]);
  const prev = useCallback(() => setIdx(i => (i <= 0 ? maxIdx : i - 1)), [maxIdx]);

  useEffect(() => {
    const id = setInterval(next, 5500);
    return () => clearInterval(id);
  }, [next]);

  const widthPercent = 100 / perView;

  return (
    <section className="py-20 bg-[#15348C] text-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#6BBE32]/15 rounded-full blur-3xl"/>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F57C20]/15 rounded-full blur-3xl"/>
      <div className="relative max-w-7xl mx-auto px-4">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">TESTIMONIALS</div>
              <h2>What our learners say</h2>
            </div>
            <div className="flex gap-2">
              <button onClick={prev} aria-label="Previous" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#6BBE32] flex items-center justify-center transition-colors"><ChevronLeft size={18}/></button>
              <button onClick={next} aria-label="Next" className="w-11 h-11 rounded-full bg-white/10 hover:bg-[#6BBE32] flex items-center justify-center transition-colors"><ChevronRight size={18}/></button>
            </div>
          </div>
        </Reveal>

        <div className="overflow-hidden">
          <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${idx * widthPercent}%)` }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="shrink-0 px-3" style={{ width: `${widthPercent}%` }}>
                <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 h-full flex flex-col">
                  <Quote size={28} className="text-[#8BD835] mb-4"/>
                  <p className="text-gray-200 leading-relaxed flex-1">“{t.text}”</p>
                  <div className="mt-6 pt-5 border-t border-white/10 flex items-center gap-3">
                    <img src={t.avatar} alt={t.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#8BD835]"/>
                    <div>
                      <div className="font-semibold">{t.name}</div>
                      <div className="text-xs text-gray-300">{t.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex justify-center gap-1.5">
          {Array.from({ length: maxIdx + 1 }).map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i+1}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-[#8BD835] w-8" : "bg-white/30 w-2"}`}/>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CTABanner = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4">
      <Reveal variant="scale">
        <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/25 rounded-full blur-3xl"/>
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-white leading-tight">Ready to upskill your team for the AI era?</h2>
              <p className="mt-3 text-gray-200">Get a custom enterprise learning plan tailored to your business goals. Free consultation, no commitment.</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <a href="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#F57C20] transition-colors">Talk to Our Advisor</a>
              <a href="/e-learning" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#F57C20] transition-colors">Browse Courses</a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
